import { useCallback, useEffect, useState } from "react";
import {
  STORAGE_KEY,
  elapsedSeconds,
  isFinished,
  parseStoredSession,
  remainingSeconds,
} from "../../lib/focusTimer";

function readStored() {
  try {
    return parseStoredSession(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

function writeStored(session) {
  try {
    if (session) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Tarayıcı depolaması kapalıysa seans yalnız bu sekmede sürer.
  }
}

/**
 * Odak sayacı (web). Başlangıç anı saklanır, gösterilen süre her seferinde
 * saatten hesaplanır: sekme arka plandayken tarayıcı zamanlayıcıyı yavaşlatsa
 * da süre doğru kalır, sekmeye dönünce hemen yerine oturur. Seans tarayıcıda
 * saklanır; sayfa yenilense de kaldığı yerden sürer. Seans sürerken sayfayı
 * kapatmaya çalışınca tarayıcı uyarır.
 */
export default function useFocusTimer() {
  const [session, setSession] = useState(readStored);
  const [now, setNow] = useState(() => Date.now());

  // Ekrandaki sayı saniyede bir tazelenir; süre buradan değil saatten gelir.
  useEffect(() => {
    if (!session) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    const onVisible = () => setNow(Date.now());
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [session]);

  // Seans sürerken sayfayı kapatma / yenileme uyarısı.
  useEffect(() => {
    if (!session) return undefined;
    const onBeforeUnload = (event) => {
      event.preventDefault();
      // Eski tarayıcılar uyarıyı ancak returnValue ile gösteriyor.
      event.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [session]);

  // Başka sekmede başlatılan ya da bitirilen seans burada da görünsün.
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === STORAGE_KEY) setSession(parseStoredSession(event.newValue));
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const start = useCallback((next) => {
    writeStored(next);
    setSession(next);
    setNow(Date.now());
    return next;
  }, []);

  const stop = useCallback(() => {
    writeStored(null);
    setSession(null);
  }, []);

  return {
    session,
    now,
    elapsed: session ? elapsedSeconds(session, now) : 0,
    remaining: session ? remainingSeconds(session, now) : 0,
    finished: session ? isFinished(session, now) : false,
    start,
    stop,
  };
}
